import { apiFetch } from "../api/client";
import type {
  AssistantTurnsPage,
  KnownContact,
  CallLogEntry,
  ChatAnswer,
  ChatStatus,
  ChatTurnIn,
  Client,
  FollowedRole,
  GridUpload,
  ListRolesOptions,
  MatchedRolesPage,
  NewClient,
  Project,
  ProjectAlias,
  ProjectUpdate,
  ProposedChange,
  RoleCandidate,
  RoleOptions,
  RoleWithProject,
  RolesPage,
  StatusTag,
} from "../api/types";
import { statusTags } from "../lib/project";
import type { DataSource } from "./source";

type ApiProject = {
  id: string;
  title: string;
  project_type: string | null;
  status_raw: string | null;
  // Optional only so a page served by an API from before this field existed
  // still draws. Every current API sends it.
  status_tags?: StatusTag[];
  canonical_stage: string | null;
  studio_network: string | null;
  network: string | null;
  networks?: string[];
  production_company: string | null;
  location: string | null;
  genres: string[];
  budget: string | null;
  synopsis: string | null;
  start_date: string | null;
  wrap_date: string | null;
  start_date_sort?: string | null;
  start_date_precision?: string | null;
  start_date_as_written: string | null;
  wrap_date_as_written: string | null;
  source_agency: string | null;
  key_people: string[];
  directors: string[];
  producers: string[];
  casting_associates: string[];
  casting_assistants: string[];
  production_executives: string[];
  aliases: ProjectAlias[];
};

type ApiRoleWithProject = Omit<RoleWithProject, "project"> & { project: ApiProject };
type ApiMatchedRolesPage = Omit<MatchedRolesPage, "items"> & { items: ApiRoleWithProject[] };
type ApiRolesPage = Omit<RolesPage, "items"> & { items: ApiRoleWithProject[] };

function mapProject(project: ApiProject): Project {
  const genres = project.genres ?? [];
  return {
    id: project.id,
    title: project.title,
    project_type: project.project_type,
    status_raw: project.status_raw,
    status_tags:
      project.status_tags ??
      // Unclassified rather than guessed. The tone table draws an unknown tag
      // exactly as it drew any tag it did not recognise, so this fallback
      // looks the same as before and claims nothing about what the tag is.
      statusTags(project.status_raw).map((label) => ({ label, kind: "unknown" as const })),
    canonical_stage: project.canonical_stage,
    studio: project.studio_network,
    network: project.network,
    networks: project.networks ?? [],
    production_company: project.production_company,
    location: project.location,
    genres,
    genre: genres.length ? genres.join(", ") : null,
    aliases: project.aliases ?? [],
    budget: project.budget,
    synopsis: project.synopsis,
    source_agency: project.source_agency ?? "unknown",
    start_date: project.start_date,
    wrap_date: project.wrap_date,
    start_date_sort: project.start_date_sort ?? null,
    start_date_precision: project.start_date_precision ?? null,
    start_date_as_written: project.start_date_as_written,
    wrap_date_as_written: project.wrap_date_as_written,
    key_people: project.key_people,
    directors: project.directors,
    producers: project.producers,
    casting_associates: project.casting_associates ?? [],
    casting_assistants: project.casting_assistants ?? [],
    production_executives: project.production_executives ?? [],
  };
}

function mapRole(role: ApiRoleWithProject): RoleWithProject {
  return { ...role, project: mapProject(role.project) };
}

function mapPage(page: ApiMatchedRolesPage): MatchedRolesPage {
  return { ...page, items: page.items.map(mapRole) };
}

function mapRolesPage(page: ApiRolesPage): RolesPage {
  return { ...page, items: page.items.map(mapRole) };
}

/** A call as the API stores it. The column is channel, and lowercase. */
type ApiCall = Omit<CallLogEntry, "kind"> & { channel: string };

/**
 * The API calls it channel, the screens call it kind, and the two never met.
 *
 * Nothing translated between them, so reading a project with a call in its log
 * gave every row a kind of undefined and the project page threw on the first
 * one it tried to draw. Writing was wrong in the other direction and quieter:
 * the body carried kind, the API read channel, found nothing, and fell back to
 * its default, so an email logged through the assistant was stored as a phone
 * call and nothing said otherwise.
 */
function mapCall(entry: ApiCall): CallLogEntry {
  const { channel, ...rest } = entry;
  return { ...rest, kind: channel === "email" ? "Email" : "Phone" };
}

function callBody(entry: Omit<CallLogEntry, "id">): Record<string, unknown> {
  const { kind, ...rest } = entry;
  return { ...rest, channel: kind === "Email" ? "email" : "phone" };
}

function rolesQuery(options: ListRolesOptions = {}): string {
  const params = new URLSearchParams();
  params.set("limit", String(options.limit ?? 50));
  params.set("offset", String(options.offset ?? 0));
  if (options.q?.trim()) params.set("q", options.q.trim());
  if (options.role_type) params.set("role_type", options.role_type);
  // Repeated, one per key, which is how the endpoint reads a multi-select: any
  // of the keys matches rather than all of them.
  for (const key of options.role_type_key ?? []) params.append("role_type_key", key);
  if (options.project_type) params.set("project_type", options.project_type);
  if (options.gender) params.set("gender", options.gender);
  if (options.location) params.set("location", options.location);
  if (options.status) params.set("status", options.status);
  if (options.added_within_days != null) {
    params.set("added_within_days", String(options.added_within_days));
  }
  return params.toString();
}

/**
 * The real data source.
 *
 * None of these endpoints exist yet; they arrive once the schema is agreed.
 * The paths are written out so the shape of the contract is visible and the
 * switch is a one line change in index.ts rather than a rewrite of screens.
 */
export const apiSource: DataSource = {
  listClients: () => apiFetch<Client[]>("/clients"),
  getClient: (id) => apiFetch<Client | null>(`/clients/${id}`),
  addClient: (client: NewClient) =>
    apiFetch<Client>("/clients", { method: "POST", body: JSON.stringify(client) }),
  updateClient: (id, client: NewClient) =>
    apiFetch<Client>(`/clients/${id}`, { method: "PUT", body: JSON.stringify(client) }),
  deleteClient: (id) => apiFetch<void>(`/clients/${id}`, { method: "DELETE" }),
  listMatchedRoles: (clientId, options = {}) => {
    const params = new URLSearchParams();
    params.set("limit", String(options.limit ?? 50));
    params.set("offset", String(options.offset ?? 0));
    if (options.q?.trim()) params.set("q", options.q.trim());
    if (options.read !== undefined) params.set("read", String(options.read));
    return apiFetch<ApiMatchedRolesPage>(
      `/clients/${clientId}/matched-roles?${params.toString()}`,
    ).then(mapPage);
  },
  listTrackedRoles: (clientId) =>
    apiFetch<ApiRoleWithProject[]>(`/clients/${clientId}/tracked-roles`).then((roles) =>
      roles.map(mapRole),
    ),
  listAllTrackedRoles: () => apiFetch<FollowedRole[]>("/tracked-roles"),
  listRoles: (options = {}) =>
    apiFetch<ApiRolesPage>(`/roles?${rolesQuery(options)}`).then(mapRolesPage),
  listRoleOptions: () => apiFetch<RoleOptions>("/roles/options"),
  listGenres: () => apiFetch<string[]>("/genres"),
  listProjectRoles: (projectId) =>
    apiFetch<ApiRoleWithProject[]>(`/projects/${projectId}/roles`).then((roles) =>
      roles.map(mapRole),
    ),
  getRole: (id) => apiFetch<ApiRoleWithProject | null>(`/roles/${id}`).then((role) =>
    role ? mapRole(role) : null,
  ),
  listUpdates: (clientId) =>
    apiFetch<ProjectUpdate[]>(
      clientId ? `/updates?client_id=${encodeURIComponent(clientId)}` : "/updates",
    ),
  listCalls: (projectId) =>
    apiFetch<ApiCall[]>(`/projects/${projectId}/calls`).then((rows) => rows.map(mapCall)),
  addCall: (entry) =>
    apiFetch<ApiCall>(`/projects/${entry.project_id}/calls`, {
      method: "POST",
      body: JSON.stringify(callBody(entry)),
    }).then(mapCall),
  setTracked: (clientId, roleId, tracked) =>
    apiFetch<void>(`/clients/${clientId}/roles/${roleId}/tracked`, {
      method: "PUT",
      body: JSON.stringify({ tracked }),
    }),
  setSeen: (clientId, projectId, seen) =>
    apiFetch<void>(`/clients/${clientId}/projects/${projectId}/seen`, {
      method: "PUT",
      body: JSON.stringify({ seen }),
    }),
  chatStatus: () => apiFetch<ChatStatus>("/chat/status"),
  listAssistantTurns: (options = {}) => {
    const params = new URLSearchParams();
    params.set("limit", String(options.limit ?? 50));
    params.set("offset", String(options.offset ?? 0));
    if (options.q?.trim()) params.set("q", options.q.trim());
    return apiFetch<AssistantTurnsPage>(`/assistant/turns?${params.toString()}`);
  },
  listKnownContacts: (projectId) =>
    apiFetch<KnownContact[]>(`/projects/${projectId}/known-contacts`),
  listRoleCandidates: (roleId) =>
    apiFetch<RoleCandidate[]>(`/roles/${roleId}/candidates`),
  askAssistant: (messages: ChatTurnIn[], confirmWrites: boolean) =>
    apiFetch<ChatAnswer>("/chat", {
      method: "POST",
      body: JSON.stringify({ messages, confirm_writes: confirmWrites }),
    }),
  proposeProjectChange: (text: string) =>
    apiFetch<ProposedChange>("/updates/propose", {
      method: "POST",
      body: JSON.stringify({ text }),
    }),
  applyProjectChange: (change: ProposedChange) =>
    apiFetch<void>("/updates/apply", {
      method: "POST",
      body: JSON.stringify(change),
    }),
  markListSeen: (clientId, seen, q) => {
    const params = new URLSearchParams();
    if (q?.trim()) params.set("q", q.trim());
    const query = params.toString();
    return apiFetch<{ projects: number; changed: number }>(
      `/clients/${clientId}/matched-roles/seen${query ? `?${query}` : ""}`,
      { method: "PUT", body: JSON.stringify({ seen }) },
    );
  },
  uploadGrid: (file, agency) => {
    const form = new FormData();
    form.append("file", file);
    form.append("source_agency", agency);
    return apiFetch<GridUpload>("/grids", { method: "POST", body: form });
  },
};
